"use client";

import type { Flashcard } from "@/hooks/useFlashcards";
import { getFlashcardLocale } from "@/lib/flashcards/pronunciation";
import { getSavedFlashcardBack, getSavedFlashcardFront } from "@/lib/sessionVocabulary";
import { ArrowRight, BookOpen, Heart, Play, Sparkles } from "lucide-react";
import * as React from "react";
import { FlashcardReviewCard } from "./FlashcardReviewCard";

interface FlashcardLibraryProps {
  flashcards: Flashcard[];
  onToggleFavorite: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  filterLanguage?: string;
  showFavoritesOnly?: boolean;
  filterType?: "all" | "generated" | "imported";
  filterDeck?: string;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  onStartSpeaking?: () => void;
}

const cardDateFormatter = new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

export function FlashcardLibrary({
  flashcards,
  onToggleFavorite,
  onDelete,
  filterLanguage,
  showFavoritesOnly = false,
  filterType = "all",
  filterDeck,
  hasActiveFilters = false,
  onClearFilters,
  onStartSpeaking,
}: FlashcardLibraryProps) {
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
  const libraryHeadingRef = React.useRef<HTMLHeadingElement>(null);

  const filteredFlashcards = React.useMemo(() => {
    let filtered = flashcards;

    if (filterLanguage) {
      filtered = filtered.filter((card) => card.target_language === filterLanguage);
    }
    if (showFavoritesOnly) {
      filtered = filtered.filter((card) => card.is_favorite);
    }
    if (filterType !== "all") {
      filtered = filtered.filter((card) => (card.card_type || "generated") === filterType);
    }
    if (filterDeck) {
      filtered = filtered.filter((card) => card.source_deck_id === filterDeck);
    }

    return filtered;
  }, [flashcards, filterDeck, filterLanguage, filterType, showFavoritesOnly]);

  React.useEffect(() => {
    if (selectedIndex !== null && selectedIndex >= filteredFlashcards.length) {
      setSelectedIndex(null);
    }
  }, [filteredFlashcards.length, selectedIndex]);

  const handleNext = () => {
    if (selectedIndex !== null && selectedIndex < filteredFlashcards.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  if (filteredFlashcards.length === 0) {
    const filtersRemovedEverything = flashcards.length > 0 && hasActiveFilters;

    return (
      <section className="flex min-h-72 flex-col items-center justify-center border-y border-white/10 px-4 py-12 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-md bg-orange-500 text-white">
          {filtersRemovedEverything ? <Sparkles size={20} /> : <BookOpen size={20} />}
        </span>
        <h2
          ref={libraryHeadingRef}
          tabIndex={-1}
          className="mt-4 text-xl font-bold text-white outline-none"
        >
          {filtersRemovedEverything ? "No cards match" : "No flashcards yet"}
        </h2>
        <button
          type="button"
          onClick={filtersRemovedEverything ? onClearFilters : onStartSpeaking}
          className="mt-5 inline-flex h-10 items-center justify-center rounded-md bg-orange-500 px-4 text-sm font-bold text-white transition-colors hover:bg-orange-600"
        >
          {filtersRemovedEverything ? "Clear filters" : "Start speaking"}
        </button>
      </section>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2
            ref={libraryHeadingRef}
            tabIndex={-1}
            className="text-base font-bold text-white outline-none"
          >
            Your review deck
          </h2>
          <p className="mt-0.5 text-sm text-white/45">
            {filteredFlashcards.length} card{filteredFlashcards.length === 1 ? "" : "s"} ready
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSelectedIndex(0)}
          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-md bg-orange-500 px-4 text-sm font-bold text-white transition-colors hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
        >
          <Play size={15} fill="currentColor" />
          Review cards
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filteredFlashcards.map((flashcard, index) => {
          const front = getSavedFlashcardFront(flashcard);
          const back = getSavedFlashcardBack(flashcard);
          const isImported = flashcard.card_type === "imported";

          return (
            <button
              key={flashcard.id}
              type="button"
              onClick={() => setSelectedIndex(index)}
              className="group flex min-h-44 flex-col rounded-lg border border-white/10 bg-[#15151a] p-4 text-left transition-colors hover:border-orange-400/35 hover:bg-[#18181e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
              aria-label={`Review ${flashcard.target_language}: ${front}`}
            >
              <div className="flex items-center justify-between gap-3 text-xs font-semibold">
                <div className="min-w-0 truncate text-white/45">
                  <span className="text-orange-300">{flashcard.target_language}</span>
                  {isImported ? <span> / Imported</span> : null}
                </div>
                {flashcard.is_favorite ? (
                  <Heart
                    size={15}
                    className="shrink-0 fill-orange-400 text-orange-400"
                    aria-label="Favorite"
                  />
                ) : null}
              </div>

              <p
                lang={getFlashcardLocale(flashcard.target_language)}
                className="mt-5 line-clamp-3 text-lg font-semibold leading-snug text-white transition-colors group-hover:text-orange-100"
              >
                {front}
              </p>
              {back ? (
                <p className="mt-2 line-clamp-1 text-sm text-white/42">{back}</p>
              ) : (
                <span className="mt-2 text-sm text-white/25">No translation</span>
              )}

              <div className="mt-auto flex items-end justify-between gap-3 pt-5 text-xs text-white/35">
                <span className="min-w-0 truncate">
                  {flashcard.source_deck_name ||
                    cardDateFormatter.format(new Date(flashcard.created_at))}
                </span>
                <ArrowRight
                  size={16}
                  className="shrink-0 text-white/30 transition-transform group-hover:translate-x-0.5 group-hover:text-orange-300"
                />
              </div>
            </button>
          );
        })}
      </div>

      {selectedIndex !== null && filteredFlashcards[selectedIndex] && (
        <FlashcardReviewCard
          flashcard={filteredFlashcards[selectedIndex]}
          onClose={() => setSelectedIndex(null)}
          onNext={selectedIndex < filteredFlashcards.length - 1 ? handleNext : undefined}
          onPrevious={selectedIndex > 0 ? handlePrevious : undefined}
          onToggleFavorite={onToggleFavorite}
          onDelete={onDelete}
          currentIndex={selectedIndex}
          totalCount={filteredFlashcards.length}
          fallbackFocusRef={libraryHeadingRef}
        />
      )}
    </>
  );
}
